import React, { useEffect, useState } from "react";
import { DoorOpen as DoorOpenIcon, CalendarCheck as CalendarCheckIcon, CalendarX as CalendarXIcon } from "lucide-react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    CircularProgress,
    IconButton,
    Paper,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format } from "date-fns";
import { Calendar } from "../../../../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../../../components/ui/popover";
import { cn } from "../../../../../../lib/utils";
import styles from "./UpdateRoomAvailability.module.scss";
import { getViewRoomDuration, updateRoomDuration } from "../../../../../../api/services/hostService";
import { useUserContext } from "../../../../../protectedRoute/UserContext";
import dayjs, { Dayjs } from "dayjs";
import { IViewRoomDurationReponse } from "../../../../../model/hostModel";

interface Room {
    id: number;
    room_number: string | null;
    start_date: Dayjs | null;
    end_date: Dayjs | null;
    property?: string;
}

interface UpdateRoomAvailabilityProps {
    room?: any;
    onRoomAvailabilityUpdated: () => void;
    onCancel?: () => void;
}

const UpdateRoomAvailability: React.FC<UpdateRoomAvailabilityProps> = ({ room, onRoomAvailabilityUpdated, onCancel }) => {
    const [rooms, setRooms] = useState<Room[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRoom, setEditingRoom] = useState<Room | null>(() =>
        room ? {
            id: room.id,
            room_number: room.room_number || null,
            start_date: room.start_date ? dayjs(room.start_date) : null,
            end_date: room.end_date ? dayjs(room.end_date) : null,
            property: room.property,
        } : null
    );
    const [loading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { state } = useUserContext();
    const { propertyID } = state;

    // Fetch initial data (only used for standalone table view when no room prop is passed)
    useEffect(() => {
        const fetchRooms = async () => {
            if (!propertyID) {
                console.error("propertyID is null or undefined.");
                return;
            }

            setLoading(true);
            try {
                const fetchedRooms: IViewRoomDurationReponse[] = await getViewRoomDuration(propertyID);
                const processedRooms = fetchedRooms.map((room) => ({
                    id: room.id,
                    room_number: room.room_number || null,
                    start_date: room.start_date ? dayjs(room.start_date) : null,
                    end_date: room.end_date ? dayjs(room.end_date) : null
                }));
                setRooms(processedRooms);
            } catch (error) {
                console.error("Failed to fetch rooms:", error);
            } finally {
                setLoading(false);
            }
        };

        if (!room) {
            fetchRooms();
        }
    }, [propertyID]);

    const handleEditRoom = (room: Room) => {
        setEditingRoom(room);
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setEditingRoom(null);
    };

    const handleSaveRoom = async () => {
        if (editingRoom && propertyID) {
            try {
                // Format the start_date and end_date to 'YYYY-MM-DD'
                const startDate = editingRoom.start_date ? editingRoom.start_date.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
                const endDate = editingRoom.end_date ? editingRoom.end_date.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    
                await updateRoomDuration(editingRoom.id, {
                    room: editingRoom.room_number!,
                    start_date: startDate,
                    end_date: endDate,
                });
                onRoomAvailabilityUpdated();
            } catch (error) {
                console.error("Failed to save room:", error);
            } finally {
                handleDialogClose();
            }
        }
    };
    

    if (loading) {
        return <CircularProgress />;
    }

    if (room) {
        return (
            <div className="space-y-5 py-2">
                {/* Room info summary */}
                <div className="bg-muted/50 rounded-lg p-4 flex items-center gap-4">
                    <div className="p-3 bg-primary/10 rounded-lg">
                        <DoorOpenIcon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                        <p className="font-semibold">Room {editingRoom?.room_number || "—"}</p>
                    </div>
                </div>

                {/* Date inputs */}
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                            <CalendarCheckIcon className="h-4 w-4 text-green-600" />
                            Available From
                        </label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    className={cn(
                                        "w-full flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-left hover:bg-accent transition-colors",
                                        !editingRoom?.start_date && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarCheckIcon className="h-4 w-4 text-green-600 shrink-0" />
                                    {editingRoom?.start_date
                                        ? format(editingRoom.start_date.toDate(), "PPP")
                                        : "Pick a start date"}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={editingRoom?.start_date ? editingRoom.start_date.toDate() : undefined}
                                    onSelect={(date) =>
                                        setEditingRoom((prev) =>
                                            prev ? { ...prev, start_date: date ? dayjs(date) : null } : null
                                        )
                                    }
                                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium">
                            <CalendarXIcon className="h-4 w-4 text-red-500" />
                            Available Until
                        </label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <button
                                    className={cn(
                                        "w-full flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-left hover:bg-accent transition-colors",
                                        !editingRoom?.end_date && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarXIcon className="h-4 w-4 text-red-500 shrink-0" />
                                    {editingRoom?.end_date
                                        ? format(editingRoom.end_date.toDate(), "PPP")
                                        : "Pick an end date"}
                                </button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={editingRoom?.end_date ? editingRoom.end_date.toDate() : undefined}
                                    onSelect={(date) =>
                                        setEditingRoom((prev) =>
                                            prev ? { ...prev, end_date: date ? dayjs(date) : null } : null
                                        )
                                    }
                                    disabled={(date) =>
                                        date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                                        (editingRoom?.start_date ? date <= editingRoom.start_date.toDate() : false)
                                    }
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                {/* Tip */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                        💡 <strong>Tip:</strong> Setting dates will automatically list your room for travelers to book.
                    </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 rounded-md border border-input text-sm font-medium hover:bg-muted transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSaveRoom}
                        disabled={isSaving}
                        className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
                    >
                        {isSaving ? "Saving..." : (editingRoom?.start_date && editingRoom?.end_date ? "Update & List" : "Save Changes")}
                    </button>
                </div>
            </div>
        )
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box className="p-6 bg-white rounded-lg shadow-lg">
                <Typography variant="h4" sx={{ color: '#1B3A32', fontWeight: 'bold', mb: 3 }}>
                    Room Availability
                </Typography>

                <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: '8px', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>Room Number</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>From Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>To Date</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', color: '#1B3A32' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rooms.map((room) => (
                                <TableRow key={room.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' }, '&:hover': { backgroundColor: '#f0f0f0' } }}>
                                    <TableCell>{room.room_number}</TableCell>
                                    <TableCell>
                                        {room.start_date
                                            ? room.start_date.format("MM/DD/YYYY")
                                            : "Not Set"}
                                    </TableCell>
                                    <TableCell>
                                        {room.end_date
                                            ? room.end_date.format("MM/DD/YYYY")
                                            : "Not Set"}
                                    </TableCell>
                                    <TableCell>
                                        <IconButton
                                            color="primary"
                                            onClick={() => handleEditRoom(room)}
                                            sx={{ color: '#1B3A32', '&:hover': { color: '#0f2d27' } }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Dialog for updating room details */}
                <Dialog open={dialogOpen} onClose={handleDialogClose}>
                    <DialogTitle sx={{ color: '#1B3A32', fontWeight: 'bold' }}>Update Room</DialogTitle>
                    <DialogContent sx={{ minWidth: 320 }}>
                        <TextField
                            fullWidth
                            margin="dense"
                            label="Room Number"
                            value={editingRoom?.room_number || ""}
                            onChange={(e) =>
                                setEditingRoom((prev) =>
                                    prev ? { ...prev, room_number: e.target.value } : null
                                )
                            }
                            sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#1B3A32' } },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#1B3A32' },
                            }}
                        />
                        <div className="space-y-3">
                            <div className="space-y-1">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <CalendarCheckIcon className="h-4 w-4 text-green-600" />
                                    From Date
                                </label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            className={cn(
                                                "w-full flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                                                !editingRoom?.start_date && "text-gray-400"
                                            )}
                                        >
                                            <CalendarCheckIcon className="h-4 w-4 text-green-600 shrink-0" />
                                            {editingRoom?.start_date
                                                ? format(editingRoom.start_date.toDate(), "PPP")
                                                : "Pick a start date"}
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={editingRoom?.start_date ? editingRoom.start_date.toDate() : undefined}
                                            onSelect={(date) =>
                                                setEditingRoom((prev) =>
                                                    prev ? { ...prev, start_date: date ? dayjs(date) : null } : null
                                                )
                                            }
                                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div className="space-y-1">
                                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                    <CalendarXIcon className="h-4 w-4 text-red-500" />
                                    To Date
                                </label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <button
                                            className={cn(
                                                "w-full flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-left hover:bg-gray-50 transition-colors",
                                                !editingRoom?.end_date && "text-gray-400"
                                            )}
                                        >
                                            <CalendarXIcon className="h-4 w-4 text-red-500 shrink-0" />
                                            {editingRoom?.end_date
                                                ? format(editingRoom.end_date.toDate(), "PPP")
                                                : "Pick an end date"}
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={editingRoom?.end_date ? editingRoom.end_date.toDate() : undefined}
                                            onSelect={(date) =>
                                                setEditingRoom((prev) =>
                                                    prev ? { ...prev, end_date: date ? dayjs(date) : null } : null
                                                )
                                            }
                                            disabled={(date) =>
                                                date < new Date(new Date().setHours(0, 0, 0, 0)) ||
                                                (editingRoom?.start_date ? date <= editingRoom.start_date.toDate() : false)
                                            }
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDialogClose} sx={{ color: '#1B3A32' }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveRoom}
                            variant="contained"
                            sx={{ backgroundColor: '#16a34a', color: 'white', '&:hover': { backgroundColor: '#15803d' } }}
                            disabled={isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} /> : "Save"}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LocalizationProvider>
    );
};

export default UpdateRoomAvailability;
